import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Warehouse } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const show = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

// Popup: push order info (clamps, spacer, codes, rim, vehicle) into the catalog item with the same CAT#.
export function CatalogSyncButton({ order, className = "", catNumber = null, preselect = null, label = "Update inventory", variant = "outline", testid = "catalog-sync-open" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant={variant} onClick={() => setOpen(true)} data-testid={testid} className={className}
              title="Update the Inventory catalog item from this order's info">
        <Warehouse size={14} className="mr-1" /> {label}
      </Button>
      {open && <CatalogSyncDialog order={order} open={open} onOpenChange={setOpen} catNumber={catNumber} preselect={preselect} />}
    </>
  );
}

export function CatalogSyncDialog({ order, open, onOpenChange, catNumber = null, preselect = null }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [picked, setPicked] = useState(null);
  const [signature, setSignature] = useState("");
  const [note, setNote] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["catalog-sync", order.id, order.updated_at, catNumber],
    queryFn: async () => (await api.get(`/orders/${order.id}/catalog-sync`, { params: catNumber ? { cat_number: catNumber } : {} })).data,
    enabled: open,
  });
  useEffect(() => {
    if (data && picked === null) {
      const base = data.fields.filter((f) => f.suggested).map((f) => f.key);
      setPicked(new Set([...base, ...(preselect || [])]));
    }
  }, [data, picked, preselect]);
  const rows = data?.fields || [];
  const vehicleRow = rows.find((f) => f.key === "vehicle");
  const vehicleApplies = !!vehicleRow?.order_value && (data?.exists ? !!vehicleRow?.differs : true);
  const vehicleOn = !!picked?.has("vehicle") && vehicleApplies;
  const needsSignature = data?.exists && vehicleOn;
  const fieldsToSend = () => [...(picked || [])].filter((k) => k !== "vehicle" || vehicleApplies);
  const apply = useMutation({
    mutationFn: async () => (await api.post(`/orders/${order.id}/catalog-sync`, {
      fields: data?.exists ? fieldsToSend() : [],
      catalog_number: catNumber || undefined,
      employee_signature: needsSignature ? signature.trim() : undefined,
      note: note.trim() || undefined,
    })).data,
    onSuccess: (r) => {
      qc.invalidateQueries();
      const parts = [];
      const others = (r.filled || []).filter((k) => k !== "vehicles").length;
      if (r.created) parts.push(`Catalog item ${r.cat_number} created from this order`);
      else if (r.vehicle_change) parts.push(`Catalog ${r.cat_number} now fits ${vehicleRow?.order_value} (${r.vehicle_change})${others ? ` · +${others} field(s)` : ""}`);
      else if (others) parts.push(`Catalog ${r.cat_number} updated · ${others} field(s)`);
      else parts.push("Catalog already up to date");
      if (r.assigned) parts.push(`CAT # ${r.cat_number} assigned to ${order.po_number}`);
      toast.success(parts.join(" · "));
      onOpenChange(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update inventory"),
  });
  const toggle = (k) => setPicked((p) => { const n = new Set(p || []); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const withValue = rows.filter((f) => f.order_value !== null && f.order_value !== undefined);
  const disabled = !data?.cat_number || apply.isPending || (data?.exists && !fieldsToSend().length) || (!data?.exists && withValue.length === 0) || (needsSignature && !signature.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="catalog-sync-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Warehouse size={18} /> Update inventory from order {order.po_number}</DialogTitle>
        </DialogHeader>
        {isLoading && <div className="text-sm text-slate-500">Loading…</div>}
        {data && !data.cat_number && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="catalog-sync-no-cat">
            This order has no Catalog # yet — assign one first.
          </div>
        )}
        {data && data.cat_number && !data.exists && (
          <div className="text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="catalog-sync-create">
            CAT # <span className="font-mono font-bold">{data.cat_number}</span> is not in Inventory. It will be created from this order
            with the Factory 1 codes, clamp types, spacer, rim size and vehicle below (Factory 2 codes stay empty, stock 0).
          </div>
        )}
        {data?.assign_on_apply && data.exists && (
          <div className="text-sm text-slate-700 bg-sky-50 border border-sky-200 rounded-md p-3" data-testid="catalog-sync-assign-note">
            CAT # <span className="font-mono font-bold">{data.cat_number}</span> will be <b>assigned to this order</b> after the update.
          </div>
        )}
        {data && data.cat_number && (
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="catalog-sync-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  {data.exists && <th className="px-3 py-2 w-8" />}
                  <th className="px-3 py-2 text-left">Field</th>
                  <th className="px-3 py-2 text-left">Order</th>
                  <th className="px-3 py-2 text-left">Catalog {data.cat_number}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => {
                  const on = picked?.has(f.key);
                  const isVehicle = f.key === "vehicle";
                  const noChange = isVehicle && data.exists && !f.differs;
                  return (
                    <tr key={f.key} className={`border-t ${f.differs ? "" : "text-slate-400"} ${isVehicle && on && f.differs ? "bg-amber-50" : ""}`} data-testid={`catalog-sync-row-${f.key}`}>
                      {data.exists && (
                        <td className="px-3 py-1.5">
                          <Checkbox checked={!!on && !noChange} disabled={f.order_value === null || f.order_value === undefined || noChange} onCheckedChange={() => toggle(f.key)} data-testid={`catalog-sync-check-${f.key}`} />
                        </td>
                      )}
                      <td className="px-3 py-1.5 font-medium">{f.label}</td>
                      <td className="px-3 py-1.5 font-mono">{show(f.order_value)}</td>
                      <td className="px-3 py-1.5 font-mono">
                        {show(f.catalog_value)}
                        {data.exists && on && f.differs && !isVehicle && <span className="ml-2 text-[10px] text-emerald-700 font-sans">→ {show(f.order_value)}</span>}
                        {isVehicle && data.exists && f.differs && on && (
                          <div className="text-[11px] text-amber-800 font-sans font-semibold mt-0.5" data-testid="catalog-sync-vehicle-preview">→ {f.preview}</div>
                        )}
                        {noChange && f.order_value && <div className="text-[10px] text-emerald-700 font-sans mt-0.5">✓ already fits</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {data?.exists && (
          <p className="text-[11px] text-slate-500">
            Ticked fields are written to the catalog, overwriting its current value. Empty catalog fields are pre-ticked.
            The vehicle row <b>adds</b> this order's car / year to the CAT #'s "Fits vehicles" list — it never removes the cars already registered.
          </p>
        )}
        {needsSignature && (
          <div className="rounded-md border-2 border-amber-400 bg-amber-50 p-3 space-y-2" data-testid="catalog-sync-signature-box">
            <div className="text-[11px] font-black uppercase tracking-widest text-amber-800">Factory-approved fit — sign to register it on the catalog</div>
            <div className="text-xs text-amber-900">
              {vehicleRow?.order_value} will be added to CAT # <span className="font-mono font-bold">{data.cat_number}</span> ({vehicleRow?.preview}).
              From now on every order for this car matches this CAT # automatically.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder={user?.name || "Your name (signature)"} data-testid="catalog-sync-signature" />
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (e.g. factory confirmation date)" data-testid="catalog-sync-note" />
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="catalog-sync-cancel">Cancel</Button>
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="catalog-sync-apply"
                  disabled={disabled}
                  onClick={() => apply.mutate()}>
            {apply.isPending ? "Updating…" : data?.exists ? `Update ${fieldsToSend().length} field(s)${data?.assign_on_apply ? " & assign CAT #" : ""}` : "Create catalog item"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
